// Lightweight JWT decoding utils shared across apps/libs

declare const Buffer:
  | {
      from(
        input: string,
        encoding: 'base64'
      ): { toString(encoding: 'utf-8'): string };
    }
  | undefined;

export interface JwtPayload {
  'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier': string;
  'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress': string;
  'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name': string;
  'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname': string;
  'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/surname': string;
  'http://schemas.microsoft.com/ws/2008/06/identity/claims/role': string;
  email_verified: string;
  sub: string;
  email: string;
  jti: string;
  iat: number;
  permission: string[];
  corporate_id?: string;
  contractor_id?: string;
  facility_id?: string | string[];
  exp: number;
  iss: string;
  aud: string;
}

export function base64UrlDecode(input: string): string {
  const base64 = input.replace(/-/g, '+').replace(/_/g, '/');
  const padLength = (4 - (base64.length % 4)) % 4;
  const padded = base64 + '='.repeat(padLength);
  try {
    return typeof atob !== 'undefined'
      ? decodeURIComponent(
          Array.prototype.map
            .call(
              atob(padded),
              (c: string) =>
                '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
            )
            .join('')
        )
      : typeof Buffer !== 'undefined'
        ? Buffer.from(padded, 'base64').toString('utf-8')
        : '';
  } catch {
    try {
      return typeof atob !== 'undefined'
        ? atob(padded)
        : typeof Buffer !== 'undefined'
          ? Buffer.from(padded, 'base64').toString('utf-8')
          : '';
    } catch {
      return '';
    }
  }
}

export function decodeJwt<T extends JwtPayload = JwtPayload>(
  token: string | null | undefined
): T | null {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length < 2) return null;
  try {
    const payloadJson = base64UrlDecode(parts[1]);
    return JSON.parse(payloadJson) as T;
  } catch {
    return null;
  }
}

export function jwtExpiresAt(token: string | null | undefined): number | null {
  const payload = decodeJwt(token);
  return payload?.exp ?? null;
}

export function secondsUntilExpiry(
  token: string | null | undefined,
  nowSec = Math.floor(Date.now() / 1000)
): number | null {
  const exp = jwtExpiresAt(token);
  if (!exp) return null;
  return exp - nowSec;
}

export function extractClaims(token: string | null | undefined) {
  const payload = decodeJwt(token);

  const role =
    payload?.['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] ??
    null;

  const emailVerified = JSON.parse(payload?.email_verified || 'false');
  let facilityIds: string[] = [];
  if (Array.isArray(payload?.facility_id)) {
    facilityIds = (payload?.facility_id as string[]).filter(Boolean);
  } else if (typeof payload?.facility_id === 'string' && payload?.facility_id) {
    facilityIds = [payload?.facility_id as string];
  }
  const contractorId = (payload?.contractor_id as string | undefined) ?? null;
  const exp = payload?.exp ?? null;

  const corporateId = (payload?.corporate_id as string | undefined) ?? null;

  return { role, emailVerified, facilityIds, contractorId, exp, corporateId };
}
